import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { Modal } from "react-bootstrap";

import type { ProductQuestionBlockModel } from "@admin/domain/products/models/edit/ProductQuestionBlockModel";
import ProductQuestionBlockEditor, {
    type PendingProductQuestionImage,
} from "./ProductQuestionBlockEditor";

import "./styles.scss";

type EditableBlock = {
    clientId: string;
    id?: number;
    html: string;
    images: PendingProductQuestionImage[];
};

type Props = {
    defaultValue: ProductQuestionBlockModel[];
};

export type ProductQuestionBlocksHandle = {
    appendToFormData: (body: FormData) => void;
    reset: () => void;
};

const makeClientId = (): string =>
    `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const createInitialBlocks = (
    blocks: ProductQuestionBlockModel[],
): EditableBlock[] =>
    blocks.map((block) => ({
        clientId: `saved-${block.id}`,
        id: block.id,
        html: block.html,
        images: [],
    }));

const getUsedImageTokens = (html: string): Set<string> => {
    const document = new DOMParser().parseFromString(html, `text/html`);

    return new Set(
        Array.from(
            document.querySelectorAll<HTMLImageElement>(
                `img[data-product-question-image]`,
            ),
        )
            .map((image) => image.dataset.productQuestionImage)
            .filter((token): token is string => Boolean(token)),
    );
};

const ProductQuestionBlocks = forwardRef<ProductQuestionBlocksHandle, Props>(
    ({ defaultValue }, ref) => {
        const initialBlocks = useMemo(
            () => createInitialBlocks(defaultValue),
            [defaultValue],
        );
        const [blocks, setBlocks] = useState<EditableBlock[]>(initialBlocks);
        const [editorVersion, setEditorVersion] = useState(0);
        const [blockToDelete, setBlockToDelete] =
            useState<EditableBlock | null>(null);
        const blocksRef = useRef(blocks);

        useEffect(() => {
            blocksRef.current = blocks;
        }, [blocks]);

        useEffect(() => {
            return () => {
                blocksRef.current.forEach((block) =>
                    block.images.forEach((image) =>
                        URL.revokeObjectURL(image.previewUrl),
                    ),
                );
            };
        }, []);

        useImperativeHandle(
            ref,
            () => ({
                reset: (): void => {
                    blocks.forEach((block) =>
                        block.images.forEach((image) =>
                            URL.revokeObjectURL(image.previewUrl),
                        ),
                    );
                    setBlocks(initialBlocks);
                    setBlockToDelete(null);
                    setEditorVersion((version) => version + 1);
                },
                appendToFormData: (body: FormData): void => {
                    blocks.forEach((block, index) => {
                        const field = `product_question_blocks[${index}]`;
                        const usedTokens = getUsedImageTokens(block.html);

                        if (block.id) {
                            body.append(`${field}[id]`, String(block.id));
                        }

                        body.append(`${field}[html]`, block.html);

                        block.images
                            .filter((image) => usedTokens.has(image.token))
                            .forEach((image) =>
                                body.append(
                                    `${field}[images][${image.token}]`,
                                    image.file,
                                ),
                            );
                    });
                },
            }),
            [blocks, initialBlocks],
        );

        const addBlock = (): void => {
            setBlocks((current) => [
                ...current,
                {
                    clientId: makeClientId(),
                    html: ``,
                    images: [],
                },
            ]);
        };

        const updateHtml = (clientId: string, html: string): void => {
            setBlocks((current) =>
                current.map((block) => {
                    if (block.clientId === clientId) {
                        return { ...block, html };
                    }

                    return block;
                }),
            );
        };

        const addImage = (
            clientId: string,
            image: PendingProductQuestionImage,
        ): void => {
            setBlocks((current) =>
                current.map((block) => {
                    if (block.clientId === clientId) {
                        return { ...block, images: [...block.images, image] };
                    }

                    return block;
                }),
            );
        };

        const removeBlock = (block: EditableBlock): void => {
            if (block.id) {
                setBlockToDelete(block);
                return;
            }

            removeConfirmed(block);
        };

        const removeConfirmed = (block: EditableBlock): void => {
            block.images.forEach((image) =>
                URL.revokeObjectURL(image.previewUrl),
            );
            setBlocks((current) =>
                current.filter((item) => item.clientId !== block.clientId),
            );
            setBlockToDelete(null);
        };

        return (
            <section className="product-question-blocks border rounded p-4 my-8">
                <div className="d-flex align-items-center justify-content-between mb-5">
                    <div>
                        <h2 className="mb-1">Фиксированные вопросы и ответы</h2>
                    </div>

                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={addBlock}
                    >
                        + Добавить блок
                    </button>
                </div>

                {blocks.map((block, index) => (
                    <div
                        className="product-question-blocks__item"
                        key={block.clientId}
                    >
                        <div className="product-question-blocks__header">
                            <strong>Блок №{index + 1}</strong>

                            <button
                                type="button"
                                className="btn btn-sm btn-light-danger"
                                onClick={() => removeBlock(block)}
                            >
                                Удалить
                            </button>
                        </div>

                        <ProductQuestionBlockEditor
                            key={editorVersion}
                            html={block.html}
                            onChange={(html) =>
                                updateHtml(block.clientId, html)
                            }
                            onImageSelected={(image) =>
                                addImage(block.clientId, image)
                            }
                        />
                    </div>
                ))}

                {blocks.length === 0 && (
                    <div className="text-muted mb-5">
                        Добавленных блоков пока нет.
                    </div>
                )}

                {blocks.length > 0 && (
                    <div className="d-flex justify-content-end">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={addBlock}
                        >
                            + Добавить блок
                        </button>
                    </div>
                )}

                <Modal
                    centered
                    show={Boolean(blockToDelete)}
                    onHide={() => setBlockToDelete(null)}
                >
                    <Modal.Header>
                        <Modal.Title>
                            Удалить блок и все его изображения?
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Footer>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setBlockToDelete(null)}
                        >
                            Нет
                        </button>
                        <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() =>
                                blockToDelete && removeConfirmed(blockToDelete)
                            }
                        >
                            Да, удалить
                        </button>
                    </Modal.Footer>
                </Modal>
            </section>
        );
    },
);

ProductQuestionBlocks.displayName = `ProductQuestionBlocks`;

export default ProductQuestionBlocks;
